import { useMemo } from 'react'
import { useMaterialsByIds } from '@/hooks/useInventoryMaster'
import { usePoLogo, usePoTemplate } from '@/hooks/usePurchaseOrders'
import { layoutForPo, type MaterialAttributes } from '@/lib/poTemplate/layout'
import type { PurchaseOrderDetail } from '@/types/purchaseOrders'

/**
 * Everything PoDocument needs for one order besides the order itself: the
 * layout it prints with (its sent snapshot, else the current template), that
 * layout's logo, and the line materials' attributes for any `attr:` column.
 *
 * The PO detail does not carry material attributes, so they are fetched by id
 * through the materials list — only when an `attr:` column is visible.
 */
export function usePoDocumentInputs(po: PurchaseOrderDetail | undefined) {
  const template = usePoTemplate()
  const layout = po ? layoutForPo(po, template.data?.layout) : null
  const logoUrl = usePoLogo(layout?.branding.logoAssetKey)
  const needsAttributes = !!layout?.columns.some((c) => c.visible && c.key.startsWith('attr:'))
  const materials = useMaterialsByIds(needsAttributes && po ? po.lines.map((l) => l.materialId) : [])
  const attributes = useMemo<MaterialAttributes>(
    () => Object.fromEntries((materials.data?.items ?? []).map((m) => [m.id, m.attributes ?? {}])),
    [materials.data],
  )
  return {
    layout,
    logoUrl,
    attributes,
    isLoading: template.isLoading || (needsAttributes && materials.isLoading),
    error: template.error,
  }
}
